<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Fundraising Codes</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="showModal = 'add'" >
                        Add Fundraising Code
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="code">Code</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="school_name">School</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="active">Active</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="percent">Percent</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="code in codes.data" :key="code.id" class="cursor-pointer" @click="editFundraisingCode(code)">
                                <td class="text-left">
                                    {{ code.code }}
                                </td>
                                <td class="text-left">
                                    {{ code.school_name }}
                                </td>
                                <td class="text-left">
                                    {{ code.active ? 'Yes' : 'No' }}
                                </td>
                                <td class="text-left">
                                    {{ code.percent }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="codes.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <!-- Add/Edit Fundraising Code -->
            <jet-dialog-modal :show="showModal" @close="closeModal">
                <template #title>
                     <h3 class="text-2xl">{{ showModal == 'add' ? 'Add' : 'Edit' }} Fundraising Code</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="flex gap-x-4 items-center">
                            <jet-label value="Code:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.code" type="text" class="w-full"/>
                            <div class="flex flex-row items-center">
                                <toggle id="active" class="py-1" v-model="form.active" />
                                <jet-label for="active" value="Active" class="ml-2 inline-block" />
                            </div>
                        </div>
                        <div class="flex gap-x-4 items-center mb-4">
                            <div style="min-width: 60px;" />
                            <jet-input-error :message="form.errors.code" />
                        </div>

                        <div class="flex gap-x-4 items-center">
                            <jet-label value="School:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.school_name" type="text" class="w-full" />
                        </div>
                        <div class="flex gap-x-4 items-center mb-4">
                            <div style="min-width: 60px;" />
                            <jet-input-error :message="form.errors.school_name" />
                        </div>

                        <div class="flex gap-x-4 items-center">
                            <jet-label value="Percent:" class="mb-0" style="width: 60px;" />
                            <jet-input v-model="form.percent" type="number" class="w-20" />
                            <jet-input-error :message="form.errors.percent" />
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showModal === 'edit'" class="mr-2" @click="deleteFundraisingCode(form)">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Fundraising Code -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Fundraising Code
                </template>

                <template #content>
                    Are you sure you would like to delete <strong>{{ form.code }}</strong>?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteFundraisingCode">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>

        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            code: '',
            active: true,
            percent: '',
            school_name: '',
        });

        return { mainForm, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Toggle,
        Pagination,
        SortableHeader,
    },

    props: ['codes', 'search', 'sortColumn', 'sortDirection', 'paginate'],

    data() {
        return {
            showModal: false,
            showDeleteModal: false,

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.fundraising_codes.index'), { preserveState: true });
        }, 250),

        handleReset () {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        closeModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showModal = false;
        },

        editFundraisingCode(data) {
            this.form.id = data.id;
            this.form.code = data.code;
            this.form.active = data.active;
            this.form.percent = data.percent;
            this.form.school_name = data.school_name;
            this.showModal = 'edit';
        },

        submitForm() {
            if (this.showModal === 'add') {
                this.form.post(this.route('settings.fundraising_codes.store'), {
                    onSuccess: () => {
                        this.form.reset();
                        this.showModal = false;
                    }
                });
            } else {
                this.form.put(this.route('settings.fundraising_codes.update', this.form.id), {
                    onSuccess: () => this.closeModal()
                });
            }
        },

        deleteFundraisingCode(data) {
            this.form.id = data.id;
            this.form.code = data.code;
            this.showDeleteModal = true;
        },

        handleDeleteFundraisingCode() {
            this.form.delete(this.route('settings.fundraising_codes.destroy', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showModal = false;
                    this.showDeleteModal = false;
                }
            });
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },
    },
}
</script>

<style scoped>
</style>
